import classNames from 'classnames';
import { debounce } from 'lodash';
import React, { useContext, useState, useRef, useEffect } from 'react';
import { RicosContext } from 'ricos-context';
import { ReactPlayer } from 'wix-react-player';
import { DownloadIcon, Slider, SLIDER_THUMB_VISIBILITY, SLIDER_TRACK_SIZE, PlaybackIcon } from 'wix-rich-content-ui-components';
import { s as styles } from './audio.rtlignore-5bf38280.js';
import { playbackRates, downloadFile, AUDIO_ACTION_NAMES, AUDIO_BI_VALUES, AUDIO_TYPES } from './consts.js';
import AudioImage from './components/AudioImage.js';
import AudioDetails from './components/AudioDetails.js';
import AudioActionIcons from './components/AudioActionIcons.js';
import SoundControl from './components/SoundControl.js';
import ContextMenu from './components/ContextMenu.js';
import AudioDuration from './components/AudioDuration.js';
import { AUDIO_TYPE } from 'ricos-content';
import './style-inject.es-e7b07dd7.js';
import 'ricos-types';
import 'wix-rich-content-common/libs/imageUtils';
import './dataHooks.js';
import './components/DropdownMenu.js';
import 'wix-rich-content-editor-common';

const ReactPlayerWrapper = ({
  url,
  authorName,
  name,
  disableDownload,
  coverImage,
  helpers,
  isMobile,
  showControls,
  disabled,
  isLoading,
  saveDurationToData,
  isDownloadDisabled
}) => {
  const { tabIndex } = useContext(RicosContext);
  const [URL, setURL] = useState(url);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [played, setPlayed] = useState(0);
  const [volume, setVolume] = useState(1);
  const [lastVolume, setLastVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showPlaybackMenu, setShowPlaybackMenu] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [isStoppedBySeek, setIsStoppedBySeek] = useState(false);
  const [currentWidth, setCurrentWidth] = useState(0);
  const resizeWidths = { small: 320 };
  const reactPlayerSize = showControls ? "100%" : 0;
  const hasDetails = authorName || name;
  const reactPlayerRef = useRef(null);
  const [customPlayerRef, setCustomPlayerRef] = useState(null);
  const onDownload = () => downloadFile(URL, name || "untitled");
  const showMobileDownloadIcon = isMobile && hasDetails && !disableDownload && !isDownloadDisabled;
  const playBackMenuData = playbackRates.map(({ text, rate }) => ({
    text,
    onClick: () => {
      setPlaybackRate(rate);
      setShowPlaybackMenu(false);
    },
    selected: playbackRate === rate
  }));
  const contextMenuOptions = {
    download: {
      text: "Download",
      onClick: () => {
        var _a;
        (_a = helpers == null ? void 0 : helpers.onViewerAction) == null ? void 0 : _a.call(
          helpers,
          AUDIO_TYPE,
          AUDIO_ACTION_NAMES.fileDownloaded,
          AUDIO_BI_VALUES.click
        );
        onDownload();
        setShowContextMenu(false);
      },
      dataHook: "audioDownloadIcon",
      icon: DownloadIcon
    },
    playback: {
      text: "Playback Speed",
      onClick: () => {
        setShowContextMenu(false);
        setShowPlaybackMenu(true);
      },
      dataHook: "audioPlaybackIcon",
      icon: PlaybackIcon
    }
  };
  const contextMenuData = disableDownload ? [contextMenuOptions.playback] : [contextMenuOptions.download, contextMenuOptions.playback];
  const handleProgress = (state) => {
    if (!seeking) {
      setPlayed(state.played);
    }
  };
  const onEnded = () => {
    var _a;
    return (_a = helpers == null ? void 0 : helpers.onViewerAction) == null ? void 0 : _a.call(
      helpers,
      AUDIO_TYPE,
      AUDIO_ACTION_NAMES.playAudio,
      AUDIO_BI_VALUES.PLAYBACK_COMPLETED
    );
  };
  const handleDuration = (num) => {
    var _a;
    setDuration(num);
    saveDurationToData(num);
    const audioType = showControls ? AUDIO_TYPES.soundCloud : AUDIO_TYPES.custom;
    (_a = helpers == null ? void 0 : helpers.mediaPluginsDetails) == null ? void 0 : _a.call(helpers, {
      pluginId: AUDIO_TYPE,
      creator: authorName,
      title: name,
      track_duration: num == null ? void 0 : num.toString(),
      type: audioType,
      url
    });
  };
  const handleSeekChange = (num) => {
    isPlaying && setIsStoppedBySeek(true);
    handlePause();
    setSeeking(true);
    setPlayed(num);
  };
  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);
  const handleContextMenuClick = () => {
    showContextMenu || showPlaybackMenu ? onClickOutside() : setShowContextMenu(true);
  };
  const handleSeekMouseUp = (num) => {
    var _a;
    (_a = reactPlayerRef == null ? void 0 : reactPlayerRef.current) == null ? void 0 : _a.seekTo(num);
    setSeeking(false);
    isStoppedBySeek && handlePlay();
    setIsStoppedBySeek(false);
  };
  const handleMute = () => {
    if (volume !== 0) {
      setLastVolume(volume);
      setVolume(0);
    } else {
      setVolume(lastVolume);
    }
  };
  const onClickOutside = () => {
    setShowContextMenu(false);
    setShowPlaybackMenu(false);
  };
  const renderAudioDuration = () => /* @__PURE__ */ React.createElement(
    AudioDuration,
    {
      elapsed: played * duration,
      duration,
      isMobile,
      isSmallWidth: resizeWidths.small >= currentWidth
    }
  );
  useEffect(() => {
    handlePause();
    setURL(url);
  }, [showControls, url]);
  useEffect(() => {
    var _a;
    (_a = reactPlayerRef.current) == null ? void 0 : _a.seekTo(0);
  }, []);
  useEffect(() => {
    if (!(window == null ? void 0 : window.ResizeObserver) || !customPlayerRef) {
      return;
    }
    const resizer = new ResizeObserver(
      debounce((entries) => {
        setCurrentWidth(Math.round(entries[0].contentRect.width));
      }, 60)
    );
    resizer.observe(customPlayerRef);
    return () => {
      resizer.disconnect();
    };
  }, [customPlayerRef]);
  return /* @__PURE__ */ React.createElement("div", null, !showControls && /* @__PURE__ */ React.createElement(
    "div",
    {
      className: classNames(styles.customPlayer, {
        [styles.customPlayer_basic]: !hasDetails,
        [styles.customPlayer_small_fit]: resizeWidths.small >= currentWidth,
        [styles.customPlayer_mobile]: isMobile,
        [styles.customPlayer_with_image]: coverImage == null ? void 0 : coverImage.src
      }),
      ref: setCustomPlayerRef
    },
    (coverImage == null ? void 0 : coverImage.src) && /* @__PURE__ */ React.createElement(
      AudioImage,
      {
        coverImage,
        getImageUrl: helpers == null ? void 0 : helpers.getImageUrl,
        isSmallImage: !hasDetails
      }
    ),
    /* @__PURE__ */ React.createElement("div", { className: styles.track_wrapper }, hasDetails && /* @__PURE__ */ React.createElement(AudioDetails, { authorName, title: name }), showMobileDownloadIcon && /* @__PURE__ */ React.createElement(DownloadIcon, { className: styles.audio_download_icon, onClick: onDownload }), /* @__PURE__ */ React.createElement("div", { className: styles.trackContainer }, /* @__PURE__ */ React.createElement(
      AudioActionIcons,
      {
        handlePlay,
        handlePause,
        isPlaying,
        disabled,
        isLoading,
        helpers
      }
    ), !isMobile && renderAudioDuration(), /* @__PURE__ */ React.createElement("div", { className: styles.track_sliders_wrapper }, /* @__PURE__ */ React.createElement(
      Slider,
      {
        languageDir: "ltr",
        value: played,
        dataHook: "audioSlider",
        onChange: handleSeekChange,
        onChangeCommitted: handleSeekMouseUp,
        step: "any",
        min: 0,
        max: 1,
        thumbVisibility: SLIDER_THUMB_VISIBILITY.hover,
        trackSize: SLIDER_TRACK_SIZE.small,
        liveSiteWiring: true,
        ariaLabel: "audio Slider",
        tabIndex
      }
    ), /* @__PURE__ */ React.createElement(
      SoundControl,
      {
        volume,
        handleMute,
        muted: volume === 0,
        handleVolumeChange: setVolume
      }
    )), isMobile && renderAudioDuration(), !isMobile && /* @__PURE__ */ React.createElement("button", { className: styles.audio_contextMenu_wrapper, tabIndex: -1, type: "button" }, /* @__PURE__ */ React.createElement(
      ContextMenu,
      {
        onClickOutside,
        contextMenuData,
        playBackMenuData,
        showContextMenu,
        showPlaybackMenu,
        onClick: handleContextMenuClick
      }
    ))))
  ), /* @__PURE__ */ React.createElement(
    ReactPlayer,
    {
      previewAriaLabel: "Play audio",
      url: URL,
      ref: reactPlayerRef,
      playing: disabled ? false : isPlaying,
      playbackRate,
      onPause: handlePause,
      onPlay: handlePlay,
      width: reactPlayerSize,
      height: reactPlayerSize,
      controls: showControls,
      volume,
      onProgress: handleProgress,
      onDuration: handleDuration,
      "data-loaded": !isLoading,
      onEnded,
      config: {
        file: {
          forceAudio: true
        }
      }
    }
  ));
};

export { ReactPlayerWrapper as default };
//# sourceMappingURL=ReactPlayerWrapper.js.map
